import React, { useEffect, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { FaFacebookF } from 'react-icons/fa';
import { WavingHand as WavingHandIcon } from '@mui/icons-material';

import { Button, Link, TextField } from '@mui/material';

import NavBar from '../NavBar.js';
import { Footer } from '../Footer/Footer.js';

import SignInImg from './Finance-Loan-Trade-Mortgage.png';

import { SignInOrLoginRow } from './SignInOrLoginRow.js';

import { Loader } from '../Elements/Loader.js';
import { useLocalStorage } from '../../CustomHooks/useLocalStorage';
import { getOrSetAnonymousUserId } from '../../utils/index';

import {
  loggingInUser,
  loggedInUser,
  clearLoggedInErrorMessage,
  getCsrfToken,
} from '../../actions';

import { TOKEN, API_ENDPOINT, LOGOUT, handleSettingsClick } from '../../constants';
import {
  LOGIN,
  WELCOME_BACK,
  SIGN_IN,
  Forgot_Password,
  email,
  emailLabel,
  emailRequired,
  emailPattern,
  password,
  passwordLabel,
  passwordRequired,
  LOGIN_LINK_TEXT,
  EMPOWER_YOUR_FINANCIAL_DECISIONS,
  SIGN_IN_WITH_GOOGLE,
  SIGN_IN_WITH_FACEBOOK,
} from '../../constants/Forms';

import './Login.css';

export const Login = () => {
  const [userId, setUserId] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, jwt, loggedInErrorMessage, csrfToken } = useSelector(
    ({ userReducer: { loading, jwt, loggedInErrorMessage, csrfToken } }) => ({
      loading,
      jwt,
      loggedInErrorMessage,
      csrfToken,
    }),
  );

  const [value] = useLocalStorage(TOKEN);
  const anonymousId = getOrSetAnonymousUserId();

  useEffect(() => {
    if (!csrfToken) {
      dispatch(getCsrfToken());
    }
  }, [csrfToken]);

  useEffect(() => {
    // Redirect to the dashboard
    if (value || jwt) {
      if (!localStorage.getItem(TOKEN)) {
        return;
      }
      const user = atob(localStorage.getItem(TOKEN)?.split('.')?.[1]);
      const parsedUser = JSON.parse(user);

      if (parsedUser) {
        const date = new Date(parsedUser.exp * 1000);
        const now = new Date();
        if (date < now) {
          handleSettingsClick[LOGOUT]?.(dispatch, parsedUser);
          return;
        }

        navigate('/dashboard');
      } else {
        setUserId(anonymousId);
      }
    } else {
      setUserId(anonymousId);
    }
  }, [value, jwt]);

  useEffect(() => {
    if (loggedInErrorMessage) {
      setTimeout(() => {
        dispatch(clearLoggedInErrorMessage);
      }, 3000);
    }
  }, [loggedInErrorMessage]);

  const handleOnUpKeyMixPanelTracking = (formField) => {
    mixpanel.track('Login Form', {
      [formField]: getValues(formField) || formField,
      path: '/login',
      distinct_id: userId,
    });
  };

  const handleGoogleLogin = () => {
    mixpanel.track('Google Auth login', {
      path: '/auth/google',
      distinct_id: userId,
    });
    window.location.href = `${API_ENDPOINT}/auth/google`;
  };

  const handleFacebookLogin = () => {
    mixpanel.track('Facebook Auth login', {
      path: '/auth/facebook',
      distinct_id: userId,
    });
    window.location.href = `${API_ENDPOINT}/auth/facebook`;
  };

  const onSubmit = (data) => {
    mixpanel.track('Login Form', {
      action: 'Submit',
    });
    dispatch(loggingInUser);
    dispatch(loggedInUser({ user: data, csrfToken }));
    setValue(email, '');
    setValue(password, '');
  };

  return (
    <>
      <NavBar currentPath="Login" />
      <div className="login-wrapper">
        <header className="login-header-wrapper">
          {' '}
          <div className="sign-up-log-in-image-wrapper">
            <img src={SignInImg} className="sign-up-log-in-image" />
          </div>
          <form className="login-form" onSubmit={handleSubmit(onSubmit)}>
            <h5 className="login-header">
              {WELCOME_BACK} <WavingHandIcon className="waving-hand-icon" />
            </h5>{' '}
            <p className="header-detail-paragraph">{EMPOWER_YOUR_FINANCIAL_DECISIONS}</p>
            <SignInOrLoginRow currentForm={LOGIN_LINK_TEXT} userId={userId} />
            <h5 className="login-header">{SIGN_IN}</h5>{' '}
            {loggedInErrorMessage && <p className="error-message">{loggedInErrorMessage}</p>}
            <div className="login-input-wrapper">
              <TextField
                className={email}
                error={!!errors[email]}
                helperText={errors[email]?.message}
                id={email}
                name={email}
                placeholder={emailLabel}
                variant="outlined"
                autoComplete="off"
                sx={{ backgroundColor: 'rgb(240, 240, 240)', borderRadius: '10px' }}
                {...register(email, {
                  required: emailRequired,
                  pattern: emailPattern,
                })}
                onKeyUp={(e) => handleOnUpKeyMixPanelTracking(e.target.name)}
              />{' '}
            </div>
            <div className="login-input-wrapper">
              <TextField
                className={password}
                error={!!errors[password]}
                helperText={errors[password]?.message}
                id={password}
                placeholder={passwordLabel}
                name={password}
                variant="outlined"
                type={password}
                autoComplete="off"
                sx={{ backgroundColor: 'rgb(240, 240, 240)', borderRadius: '10px' }}
                {...register(password, {
                  required: passwordRequired,
                })}
              />{' '}
            </div>
            <div className="login-form-button-row">
              <Link
                variant="outline"
                sx={{ color: 'black', textDecoration: 'none' }}
                onClick={() => handleOnUpKeyMixPanelTracking('forgot_password')}
                href="/forgot_password"
              >
                {Forgot_Password}
              </Link>
            </div>
            <div className="login-button-wrapper">
              <Button className="loginBtn" disabled={loading} type="submit">
                {loading ? <Loader /> : LOGIN}
              </Button>
            </div>
            <Button
              variant="outlined"
              className="googleBtn"
              onClick={handleGoogleLogin}
              startIcon={
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  width="18"
                  height="18"
                />
              }
            >
              {SIGN_IN_WITH_GOOGLE}
            </Button>
            <Button
              variant="contained"
              className="facebookBtn"
              onClick={handleFacebookLogin}
              startIcon={<FaFacebookF />}
            >
              {SIGN_IN_WITH_FACEBOOK}
            </Button>
          </form>
        </header>
        <div className="sign-up-log-in-image-wrapper-mobile">
          <img src={SignInImg} className="sign-up-log-in-image" />
        </div>
      </div>
      <Footer currentPath={'Login'} />
    </>
  );
};
